/**
 * Renders a Serilog-style message template against a property bag.
 *
 *   renderTemplate('Failed to load {Resource}', { Resource: 'phones' })
 *     -> 'Failed to load phones'
 *
 * The template itself is kept alongside the rendered text and sent as
 * customDimensions.MessageTemplate. That is the stable grouping key the backend
 * detectors use, so the same fault with different ids collapses to one signature
 * instead of producing a new one per id.
 *
 * Placeholders that have no matching property are left as written, which makes a
 * missing property visible in the message rather than silently blank.
 */
const PLACEHOLDER = /\{(@|\$)?(\w+)(?::[^}]*)?\}/g;

export function formatValue(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    // Circular or otherwise unserialisable.
    return String(value);
  }
}

export default function renderTemplate(template, properties) {
  if (typeof template !== 'string') return '';
  if (!properties) return template;

  return template.replace(PLACEHOLDER, (match, _sigil, name) =>
    Object.prototype.hasOwnProperty.call(properties, name)
      ? formatValue(properties[name])
      : match
  );
}
